import React from 'react';
import PropTypes from 'prop-types';
import { useColors } from '../../../hooks/useColors';
import { useTextReplacement } from '../../../hooks/useTextReplacement';

/**
 * A reusable TextArea component for forms, integrated with react-hook-form.
 * Useful for collecting multi-line text input and displays validation errors.
 * Uses semantic color classes from shared configuration.
 * 
 * @param {Object} props - Component props
 * @param {string} props.label - The field label text
 * @param {string} props.name - The field name (used for form registration and error display)
 * @param {Function} props.register - The react-hook-form register function
 * @param {Object} [props.rules] - Validation rules for react-hook-form
 * @param {Object} props.errors - The errors object from react-hook-form
 * @param {string} [props.placeholder] - Placeholder text for the textarea
 * @param {number} [props.rows=3] - Number of visible text rows (defaults to 3)
 * @param {string} [props.helpText] - Help text displayed below the textarea
 * @param {string} [props.className] - Additional CSS classes
 */
const TextAreaField = ({ 
    label, 
    name, 
    register, 
    rules, 
    errors, 
    placeholder,
    rows = 3,
    helpText,
    helperText, // Accept helperText as alias for helpText
    required = false,
    className = '',
    ...props 
}) => {
    // Use helpText or helperText (helperText is deprecated, use helpText)
    const displayHelpText = helpText || helperText;
    const colors = useColors();
    const semantic = colors.semantic;
    const { replace } = useTextReplacement();

    const hasError = errors && errors[name];
    const errorMessage = hasError ? errors[name].message : null;

    return (
        <div className={`mb-4 ${className}`}>
            <label htmlFor={name} className={`block text-sm font-medium ${semantic.formLabel} mb-1`}>
                {replace(label)}
                {required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <textarea
                id={name}
                {...(register ? register(name, rules) : {})}
                rows={rows}
                className={`w-full px-3 py-2 ${semantic.formBg} ${semantic.formBorder} border rounded-md ${semantic.formText} focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    hasError ? 'border-red-500 focus:ring-red-500' : ''
                }`}
                placeholder={placeholder ? replace(placeholder) : undefined}
                aria-invalid={hasError ? 'true' : 'false'}
                aria-describedby={hasError ? `${name}-error` : displayHelpText ? `${name}-help` : null}
                {...props}
            />
            {displayHelpText && !hasError && (
                <p id={`${name}-help`} className={`mt-1 text-xs ${semantic.formLabel}`}>
                    {replace(displayHelpText)}
                </p>
            )}
            {hasError && (
                <p id={`${name}-error`} className="mt-1 text-xs text-red-500" role="alert">
                    {errorMessage}
                </p>
            )}
        </div>
    );
};

TextAreaField.propTypes = {
    label: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    register: PropTypes.func,
    rules: PropTypes.object,
    errors: PropTypes.object,
    placeholder: PropTypes.string,
    rows: PropTypes.number,
    helpText: PropTypes.string,
    required: PropTypes.bool,
    className: PropTypes.string,
};

export default TextAreaField;

